import { emptyCellDash } from "src/components";
import { ChangeEventLineItem, ChangeEventLineItemType } from "src/generated/graphqlTypes";
import { BaseChangeEventFormInput } from "./baseChangeEventForm";
import { ScopeChangesTable_LineItemTradeFragment } from "./workflows/sharedSteps/ScopeChangesTable.generated";

export enum GuidedChangeEventStep {
  SelectConfiguration = "select-configuration",
  SelectChanges = "select-changes",
  SelectOptions = "select-options",
  SelectProductToReplace = "select-product-to-replace",
  VersionUpdate = "version-update",
  DiscontinuedOptions = "discontinued-options",
  ReviewConfiguration = "review-configuration",
  ReviewImpact = "review-impact",
  Reconcile = "reconcile",
  Review = "review",
}

type ChangeEventIdFields = { [K in "id" | "dryRunChangeEventId"]: { value: BaseChangeEventFormInput[K] } };

/**
 * Which change event the downstream steps read, and how: the saved draft (from the cache mid-wizard, or the network
 * after a refresh), or the dry run a no-contract project previews with, whose id only exists in the cache and so
 * must never be requested from the server.
 */
export function changeEventQueryFor(form: ChangeEventIdFields) {
  const id = form.id.value;
  const dryRunId = form.dryRunChangeEventId.value;
  return { changeEventId: id ?? dryRunId ?? undefined, fetchPolicy: id ? "cache-first" : "cache-only" } as const;
}

/** Returns the total added, total removed, and the number of added and removed items. */
export function celisImpactTotals(celis: readonly Pick<ChangeEventLineItem, "totalCostInCents" | "typeDetail">[]) {
  // The line items are deltas, so whether they add or remove money is just their sign.
  const [positiveCelis, negativeCelis] = celis.partition((li) => li.totalCostInCents > 0);
  const totalAddedInCents = positiveCelis.sum((li) => li.totalCostInCents);
  const totalRemovedInCents = Math.abs(negativeCelis.sum((li) => li.totalCostInCents));

  const addedItems = celis.count((li) => li.typeDetail.code === ChangeEventLineItemType.Add);
  const removedItems = celis.count((li) => li.typeDetail.code === ChangeEventLineItemType.Remove);

  return {
    totalAddedInCents,
    totalRemovedInCents,
    addedItems,
    removedItems,
    netItems: addedItems - removedItems,
    changedItems: addedItems + removedItems,
  };
}

/**
 * Shows a line item's trade the same way the change event line items table does, i.e.
 * "Dallas Plumbing (Revision 2) - Item Based" when a signed contract priced the line, or
 * "Dallas Plumbing (Not Contracted)" when the trade was picked by hand.
 */
export function tradeName(lineItem: ScopeChangesTable_LineItemTradeFragment | undefined): string {
  const bcli = lineItem?.proposedBidContractLineItem;
  if (bcli) {
    const { version, bidContract } = bcli.revision;
    return `${bidContract.displayName} (Revision ${version}) - ${bcli.ofIti ? "Plan Based" : "Item Based"}`;
  }
  if (lineItem?.tradePartner) return `${lineItem.tradePartner.name} (Not Contracted)`;
  return emptyCellDash;
}
